import React from 'react';
import {
  Grid,
  Box,
  Tag,
  Spacer,
  Text,
  Button,
} from '@flexisaf/flexibull2';
import { calculateDateOffset, stringToHslColor } from 'utils/helpers';

import { FormBox, FormWrapper } from './styles';
import { getColumnStructure, formatAddress } from 'utils/processFormUtil';
import { useSelector } from 'react-redux';
import { selectInstitution } from 'redux/configurations/selectors';
import { FALL_BACK_TZ } from 'utils/constants';

const moment = require('moment-timezone');

const renderFieldValue = (
  eachField,
  submissionData,
  institution
) => {
  if (eachField?.source === 'nigeria-states-lga') {
    return formatAddress(submissionData?.[eachField.id]);
  }
  if (eachField?.type === 'checkbox') {
    return submissionData?.[eachField.id]
      ? eachField.trueDisplay
      : eachField.falseDisplay;
  }
  if (
    ['date', 'single-date'].includes(eachField.type?.toLowerCase()) &&
    submissionData?.[eachField.id]
  ) {
    return moment(
      calculateDateOffset(
        submissionData?.[eachField.id],
        institution?.timeZone
      )
    )
      .tz(institution?.timeZone || FALL_BACK_TZ)
      .format('MMMM Do YYYY');
  }
  return (
    <span
      dangerouslySetInnerHTML={{
        __html: submissionData?.[eachField.id] || '—',
      }}
    />
  );
};

export const FormPreview = ({
  onUpdateForm,
  onSubmitForm,
  processedForm,
  submissionData,
  application,
  courseId,
  processedFormAttachments,
  saving = false,
  schema,
}) => {
  const institution = useSelector(selectInstitution);
  const getCourseDetails = () => {
    const course = application.courses?.find((c) => c.id === courseId);
    return `${course?.title} - ${course?.code}`;
  };

  const filterProcessForm = () => {
    const joinedForm = processedForm.map((form, i) => ({
      ...form,
      ...schema?.layout?.sections[i],
    }));
    return joinedForm.filter(
      (form) => form?.refereeSectionType?.toLowerCase() !== 'referee'
    );
  };

  const fullName = [submissionData?.first_name, submissionData?.last_name]
    .filter(Boolean)
    .join(' ');

  const hasAttachments = processedFormAttachments?.find(
    (eachAttachment) => submissionData?.[eachAttachment.id]?.[0]?.name
  );

  return (
    <FormBox>
      <FormWrapper className="form-preview">
        <Box className="preview-hero">
          <Text className="preview-eyebrow" bold block>
            Review & confirm
          </Text>
          <Text className="preview-title" bold block>
            Application summary
          </Text>
          <Text className="preview-subtitle" block>
            Check every section below before updating this applicant’s form.
          </Text>

          <Box className="preview-identity">
            <Box className="preview-identity-copy">
              <Text className="preview-name" bold block>
                {fullName || 'Applicant'}
              </Text>
              <Text className="preview-app" bold block>
                {application?.title}
              </Text>
              <Text className="preview-course" block>
                <span>Course</span> {getCourseDetails()}
              </Text>
              {!!application?.applicationGroupItems?.length && (
                <Box className="preview-tags">
                  {application.applicationGroupItems.map((group, i) => (
                    <Tag
                      key={i}
                      height={26}
                      font="11px"
                      style={{ border: 'none', borderRadius: 999 }}
                      color={stringToHslColor(group.name, 100, 30)}
                      fontColor={stringToHslColor(group.name, 30, 40)}
                      spaceRight
                    >
                      {group.name}
                    </Tag>
                  ))}
                </Box>
              )}
            </Box>
            {submissionData?.passport_picture && (
              <Box className="preview-passport">
                <img
                  src={submissionData.passport_picture}
                  alt="Passport"
                />
              </Box>
            )}
          </Box>
        </Box>

        <Box className="preview-body applicant-info">
          {filterProcessForm()?.map((item, i) => (
            <Box className="preview-section" key={i}>
              <Text className="preview-section-title" bold block>
                {item.title}
              </Text>
              {item?.inputs?.map((eachRow, rowIndex) => (
                <Grid
                  className="form-grid"
                  default={getColumnStructure(eachRow)}
                  gap="10px"
                  rowGap="10px"
                  columnGap="10px"
                  key={`row-${rowIndex}`}
                >
                  {eachRow?.map((eachField, fieldIndex) => (
                    <Box
                      key={fieldIndex}
                      className="single-form"
                    >
                      <Text className="field-label" bold block>
                        {eachField.title}
                      </Text>
                      <Text
                        className="field-value"
                        capitalize={
                          !['email', 'wysiwyg'].includes(
                            eachField.type?.toLowerCase()
                          )
                        }
                        block
                      >
                        {renderFieldValue(
                          eachField,
                          submissionData,
                          institution
                        )}
                      </Text>
                    </Box>
                  ))}
                </Grid>
              ))}
            </Box>
          ))}

          {hasAttachments && (
            <Box className="preview-section">
              <Text className="preview-section-title" bold block>
                Attachments
              </Text>
              <Grid default="1fr 1fr 1fr" gap="12px" className="form-grid">
                {processedFormAttachments?.map((eachAttachment, i) => (
                  <Box key={i} className="single-form">
                    <Text className="field-label" bold block>
                      {eachAttachment.alternateLabel}
                    </Text>
                    <Text className="field-value" block>
                      {submissionData?.[eachAttachment.id]?.[0]?.name || '—'}
                    </Text>
                  </Box>
                ))}
              </Grid>
            </Box>
          )}
        </Box>

        <Box className="form-actions preview-actions">
          <Button
            height={46}
            pale
            color="#e8e4f0"
            width={150}
            spaceRight="12px"
            fontColor="#5c5668"
            iconLeft={<i className="saf-left-open"></i>}
            onClick={onUpdateForm}
          >
            Edit details
          </Button>
          <Button
            height={46}
            bold
            width={140}
            onClick={onSubmitForm}
            progress={saving}
          >
            Update
          </Button>
        </Box>
        <Spacer space="24px" />
      </FormWrapper>
    </FormBox>
  );
};
